import {
  Card,
  Col,
  Empty,
  Flex,
  message,
  Row,
  Statistic,
  Table,
  Tabs,
} from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { stockOverviewPrint } from "../../../components/print/stock-overview";
import SelectClient from "../../../components/SelectClient";
import SelectClientProduct from "../../../components/SelectClientProduct";
import SelectProduct from "../../../components/SelectProduct";
import API from "../../../services/api-request";
import TableColumn from "./columns";
import FilterMenu from "./filter-menu";

const StockOverview = () => {
  const [data, setData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState("Client");

  const queryParams = Object.fromEntries([...searchParams]);
  const pagination = {
    current: parseInt(searchParams.get("page")) || 1,
    limit: parseInt(searchParams.get("limit")) || 10,
    total: totalData,
    onChange: (page, limit) => {
      pagination.current = page;
      pagination.pageSize = limit;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("page", page);
      newSearchParams.set("limit", limit);
      setSearchParams(newSearchParams);
    },
  };

  const updateSearchParam = (key, value) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value !== null) {
      newSearchParams.set(key, value);
    } else {
      newSearchParams.delete(key);
    }
    setSearchParams(newSearchParams);
  };

  const updateSearchParams = (params = {}) => {
    const newSearchParams = new URLSearchParams(searchParams);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });

    setSearchParams(newSearchParams);
  };

  const getStockData = async (query) => {
    setData([]);
    const queryString = new URLSearchParams(query).toString();
    // Add default page and limit
    if (!searchParams.has("limit")) {
      searchParams.set("limit", 10);
    }
    if (!searchParams.has("page")) {
      searchParams.set("page", 1);
    }
    setSearchParams(searchParams);

    setLoading(true);
    API.Post(`dashboard/stocks${query ? "?" + queryString : ""}`)
      .then((res) => {
        const page = query.page ? parseInt(query.page) - 1 : 0;
        const limit = query.limit ? parseInt(query.limit) : 10;

        setTotalData(res?.data?.data?.total);

        const formatSelect = res?.data?.data?.stock?.map((item, index) => {
          return {
            ...item,
            no: page * limit + index + 1,
          };
        });

        setData(formatSelect);
        setTotalWeight(res?.data?.data?.weight);
        setTotalCost(res?.data?.data?.amount);
      })
      .catch((err) => {
        setData([]);
        message.info(`${err?.response?.data?.error}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const exportToExcel = async (columns) => {
    const headers = columns.map((col) => col.title);
    const dataKeys = columns.map((col) => col.dataIndex);
    const fileName = searchParams.get("client_id")
      ? `${data?.[0]?.client_name} - Stock Overview.xlsx`
      : "Semua Client - Stock Overview.xlsx";

    let url = `dashboard/stocks?limit=99999&page=1`;
    const clientId = searchParams.get("client_id");
    const productId = searchParams.get("product_id");
    const { received_at, name, sortBy, sortOrder } = queryParams;

    let error = false;
    let printData = [];

    if (clientId) {
      url += `&client_id=${clientId}`;
    }

    if (received_at) {
      url += `&received_at=${received_at}`;
    }

    if (name) {
      url += `&name=${name}`;
    }

    if (productId) {
      url += `&product_id=${productId}`;
    }

    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }

    if (sortOrder) {
      url += `&sortOrder=${sortOrder}`;
    }

    await API.Post(url)
      .then((res) => {
        const data = res?.data?.data?.stock;

        const formattedPrintData = data.map((item, index) => ({
          ...item,
          no: index + 1,
          inbound_date: dayjs(item.inbound_date).format("YYYY-MM-DD"),
          duration: item?.duration + " Hari",
          amount: "Rp " + item?.amount?.toLocaleString("id-ID"),
          weight: Math.round(item?.weight) + " Kg",
          row_number: `${item.row_number}${item.section}-${item.column_name}${item.level}`,
          specification: item?.specification ?? "-",
        }));

        printData = formattedPrintData;
      })
      .catch((err) => {
        error = true;
        console.log(err);
        message.error("Failed to print data. Try again later");
      });

    if (error) return;

    const worksheetData = [
      [`WMS ELBE GROUP`, `Stock Overview`],
      [],
      [
        selectedFilter === "Product"
          ? searchParams.get("product_id")
            ? `Jenis Produk: ${data?.[0]?.product_name}`
            : "Semua Produk"
          : searchParams.get("client_id")
            ? `Nama Client: ${data?.[0]?.client_name}`
            : "Semua Client",
        `Total Berat: ${totalWeight?.toLocaleString("id-ID")} Kg`,
        `Grand Total Biaya: Rp ${totalCost?.toLocaleString("id-ID")}`,
      ],
      [],
      headers,
      ...printData.map((row) =>
        dataKeys.map((key) => {
          const value = row[key];
          return typeof value === "object" ? JSON.stringify(value) : value;
        }),
      ),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    worksheet["!cols"] = columns.map((col) => ({
      wch: col.width ? col.width : 25,
    }));

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    if (!error) {
      saveAs(dataBlob, fileName);
    }
  };

  const exportToPDF = async () => {
    let url = `dashboard/stocks?limit=99999&page=1`;
    const clientId = searchParams.get("client_id");
    const productId = searchParams.get("product_id");
    const { received_at, name, sortBy, sortOrder } = queryParams;

    if (clientId) {
      url += `&client_id=${clientId}`;
    }

    if (received_at) {
      url += `&received_at=${received_at}`;
    }

    if (name) {
      url += `&name=${name}`;
    }

    if (productId) {
      url += `&product_id=${productId}`;
    }

    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }

    if (sortOrder) {
      url += `&sortOrder=${sortOrder}`;
    }

    let error = false;
    let printData = [];

    await API.Post(url)
      .then((res) => {
        const data = res?.data?.data?.stock;

        const formattedPrintData = data.map((item, index) => ({
          ...item,
          no: index + 1,
          inbound_date: dayjs(item.inbound_date).format("YYYY-MM-DD"),
          duration: item.duration + " Hari",
          amount: item.amount,
          weight: item.weight,
          row_number: `${item.row_number}${item.section}-${item.column_name}${item.level}`,
        }));

        printData = formattedPrintData;
      })
      .catch((err) => {
        error = true;
        console.log(err);
        message.error("Failed to print data. Try again later");
      });

    if (error) return;

    const printWindow = window.open("", "_blank", "width=800,height=600");

    printWindow.document.open();
    printWindow.document.write(
      stockOverviewPrint(
        printData,
        selectedFilter,
        searchParams.get("client_id") ? data?.[0]?.client_name : null,
      ),
    );
    setTimeout(function () {
      printWindow.document.close();
    }, 2000);

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print({ showPrintDialog: true });

      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  const handleOpenNewTab = (e, stock) => {
    e.preventDefault();
    // Save state to sessionStorage
    sessionStorage.setItem("stockDetailState", JSON.stringify(stock));
    // Open new tab
    window.open(`/#/detail-stock/${stock.name}`, "_blank");
  };

  useEffect(() => {
    if (queryParams) {
      getStockData(queryParams);
    }
  }, [searchParams]);

  const tabItems = [
    {
      key: "Client",
      label: "Client",
      children: (
        <Row gutter={[4, 4]}>
          <Col span={4}>
            <h3>Client</h3>
            <SelectClient
              onChange={(e) =>
                updateSearchParams({ client_id: e || null, product_id: null })
              }
              value={+searchParams.get("client_id") || null}
            />
          </Col>
          <Col span={4}>
            <h3>Product</h3>
            {searchParams.get("client_id") ? (
              <SelectClientProduct
                nullOptions={!searchParams.get("client_id")}
                idClient={searchParams.get("client_id") || null}
                onChange={(e) => updateSearchParam("product_id", e || null)}
                value={+searchParams.get("product_id") || null}
              />
            ) : (
              <SelectProduct
                onChange={(e) => updateSearchParam("product_id", e || null)}
                value={+searchParams.get("product_id") || null}
              />
            )}
          </Col>
          <Col span={16}>
            <Flex justify="flex-end" align="center" gap={16}>
              <Card variant="borderless" style={{ width: "25%" }}>
                <Statistic
                  title="Total Berat"
                  value={Math.round(totalWeight)?.toLocaleString("id-ID")}
                  suffix="Kg"
                />
              </Card>

              <Card variant="borderless" style={{ width: "25%" }}>
                <Statistic
                  title="Grand Total Biaya"
                  value={totalCost?.toLocaleString("id-ID")}
                  prefix={"Rp"}
                />
              </Card>
            </Flex>
          </Col>
        </Row>
      ),
    },
    {
      key: "Product",
      label: "Product",
      children: (
        <Row>
          <Col span={4}>
            <h3>Product</h3>
            <SelectProduct
              onChange={(e) => updateSearchParam("product_id", e || null)}
              value={+searchParams.get("product_id") || null}
            />
          </Col>

          <Col span={20}>
            <Flex justify="flex-end" align="center" gap={16}>
              <Card variant="borderless" style={{ width: "25%" }}>
                <Statistic
                  title="Total Berat"
                  value={Math.round(totalWeight)?.toLocaleString("id-ID")}
                  suffix="Kg"
                />
              </Card>

              {/* <Card variant="borderless" style={{ width: "25%" }}>
                <Statistic
                  title="Grand Total Biaya"
                  value={totalCost.toLocaleString("id-ID")}
                  prefix={"Rp"}
                />
              </Card> */}
            </Flex>
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <Row gutter={[8, 32]}>
      <Col span={24}>
        <Tabs
          defaultActiveKey={selectedFilter}
          items={tabItems}
          onChange={(value) => {
            setSelectedFilter(value);
            setData([]);
            setSearchParams({});
            setTotalCost(0);
            setTotalWeight(0);
          }}
        />
      </Col>

      <Col span={24}>
        <FilterMenu
          data={data}
          exportToExcel={exportToExcel}
          exportToPDF={exportToPDF}
          selectedFilter={selectedFilter}
        />
        <Table
          scroll={{ x: "max-content" }}
          loading={loading}
          columns={TableColumn(false, selectedFilter, handleOpenNewTab)}
          onChange={(pagination, filters, sorter, extra) => {
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);

              if (sorter.order) {
                newParams.set("sortBy", sorter.field);
                newParams.set(
                  "sortOrder",
                  sorter.order === "ascend" ? "asc" : "desc",
                );
              } else {
                newParams.delete("sortBy");
                newParams.delete("sortOrder");
              }

              newParams.set("page", pagination.current);
              newParams.set("limit", pagination.pageSize);

              return newParams;
            });
          }}
          dataSource={data}
          pagination={pagination}
          rowKey={(dataSource) => dataSource.no}
          locale={{
            emptyText: (
              <Empty
                description={
                  !searchParams.get("client_id") && data?.length === 0
                    ? "Please Select Client"
                    : "Data Empty"
                }
              />
            ),
          }}
        />
      </Col>
    </Row>
  );
};

export default StockOverview;
